import {isReviewEffortTier, type ReviewEffortValue} from '@github-ui/copilot-code-review-controls/review-effort'
import {
  CopilotReviewEffortAvailability,
  CopilotReviewEffort,
  type CopilotReviewerRow,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'

const COPILOT_REVIEW_EFFORT_BY_ENUM: Record<number, ReviewEffortValue | undefined> = {
  [CopilotReviewEffort.UNSPECIFIED]: undefined,
  [CopilotReviewEffort.LOW]: 'low',
  [CopilotReviewEffort.MEDIUM]: 'medium',
  [CopilotReviewEffort.HIGH]: 'high',
  [CopilotReviewEffort.AUTO]: 'auto',
}

const COPILOT_REVIEW_EFFORT_BY_NAME: Record<string, ReviewEffortValue | undefined> = {
  COPILOT_REVIEW_EFFORT_UNSPECIFIED: undefined,
  COPILOT_REVIEW_EFFORT_LOW: 'low',
  COPILOT_REVIEW_EFFORT_MEDIUM: 'medium',
  COPILOT_REVIEW_EFFORT_HIGH: 'high',
  COPILOT_REVIEW_EFFORT_AUTO: 'auto',
  low: 'low',
  medium: 'medium',
  high: 'high',
  auto: 'auto',
}

export function normalizeCopilotReviewEffort(
  effort: CopilotReviewEffort | string | number | null | undefined,
): ReviewEffortValue | undefined {
  if (effort == null || effort === '') return undefined
  if (typeof effort === 'number') return COPILOT_REVIEW_EFFORT_BY_ENUM[effort]
  return COPILOT_REVIEW_EFFORT_BY_NAME[effort]
}

const AVAILABILITY_BY_NAME: Record<string, CopilotReviewEffortAvailability> = {
  COPILOT_REVIEW_EFFORT_AVAILABILITY_UNSPECIFIED: CopilotReviewEffortAvailability.UNSPECIFIED,
  COPILOT_REVIEW_EFFORT_AVAILABILITY_AVAILABLE: CopilotReviewEffortAvailability.AVAILABLE,
  COPILOT_REVIEW_EFFORT_AVAILABILITY_COMING_SOON: CopilotReviewEffortAvailability.COMING_SOON,
  COPILOT_REVIEW_EFFORT_AVAILABILITY_PREVIEW_REQUIRED: CopilotReviewEffortAvailability.PREVIEW_REQUIRED,
  available: CopilotReviewEffortAvailability.AVAILABLE,
  coming_soon: CopilotReviewEffortAvailability.COMING_SOON,
  preview_required: CopilotReviewEffortAvailability.PREVIEW_REQUIRED,
}

function effortAvailability(
  availability: CopilotReviewEffortAvailability | string | number | null | undefined,
): CopilotReviewEffortAvailability {
  if (typeof availability === 'number') return availability
  if (typeof availability === 'string') {
    return AVAILABILITY_BY_NAME[availability] ?? CopilotReviewEffortAvailability.UNSPECIFIED
  }
  return CopilotReviewEffortAvailability.UNSPECIFIED
}

export type CopilotReviewEffortBadge = {label: string; variant: 'default'}

export function copilotReviewEffortAvailabilityBadge(
  availability: CopilotReviewEffortAvailability | string | number | null | undefined,
): CopilotReviewEffortBadge | undefined {
  switch (effortAvailability(availability)) {
    case CopilotReviewEffortAvailability.COMING_SOON:
      return {label: 'Coming soon', variant: 'default'}
    case CopilotReviewEffortAvailability.PREVIEW_REQUIRED:
      return {label: 'Preview', variant: 'default'}
    default:
      return undefined
  }
}

// Only an explicitly available effort can be requested. Values this client doesn't know parse as
// UNSPECIFIED, so they stay disabled until the client learns them.
export function isCopilotReviewEffortAvailable(
  availability: CopilotReviewEffortAvailability | string | number | null | undefined,
): boolean {
  return effortAvailability(availability) === CopilotReviewEffortAvailability.AVAILABLE
}

export function isCopilotReviewEffortPreviewRequired(
  availability: CopilotReviewEffortAvailability | string | number | null | undefined,
): boolean {
  return effortAvailability(availability) === CopilotReviewEffortAvailability.PREVIEW_REQUIRED
}

export function copilotReviewEffortLabel(
  row: CopilotReviewerRow,
  effort: CopilotReviewEffort | string | number | null | undefined,
): string | undefined {
  const copilotEffortValue = normalizeCopilotReviewEffort(effort)
  return copilotEffortValue
    ? row.reviewEffortOptions.find(option => normalizeCopilotReviewEffort(option.value) === copilotEffortValue)?.label
    : undefined
}

export function copilotReviewStatusText({
  row,
  requestedCopilotReviewEffort,
  lastCompletedCopilotReviewEffort,
  lastCompletedCopilotReviewEffortAuto,
}: {
  row: CopilotReviewerRow
  requestedCopilotReviewEffort: CopilotReviewEffort | string | number | null | undefined
  lastCompletedCopilotReviewEffort: CopilotReviewEffort | string | number | null | undefined
  lastCompletedCopilotReviewEffortAuto?: boolean
}): string | undefined {
  const requested = copilotReviewEffortLabel(row, requestedCopilotReviewEffort)?.toLowerCase()
  // A completed review ran at a tier. Auto is only ever requested, so it isn't a completed effort.
  const completedEffort = normalizeCopilotReviewEffort(lastCompletedCopilotReviewEffort)
  const completedTier = isReviewEffortTier(completedEffort)
    ? copilotReviewEffortLabel(row, completedEffort)?.toLowerCase()
    : undefined
  const completed = completedTier && lastCompletedCopilotReviewEffortAuto ? `${completedTier} (auto)` : completedTier

  if (requested && completed) return `Reviewing at ${requested} effort · Last completed: ${completed}`
  if (requested) return `Reviewing at ${requested} effort`
  if (completed) return `Reviewing · Last completed: ${completed}`
  if (row.reviewEffortOptions.length > 0) return 'Reviewing'
  return undefined
}
